import React, { useEffect, useState } from 'react';
import {
  Device,
  EnergyMeter,
  useEnergyMeterDetailQuery,
  useEnergyMeterDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

interface EnergyMeterProps {
  id: string;
}

export const EnergyMeterComponent: React.FC<EnergyMeterProps> = ({ id }) => {
  const [device, setDevice] = useState<Partial<EnergyMeter>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const { data } = useEnergyMeterDetailQuery({
    variables: { id },
  });

  useEnergyMeterDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energyMeterById) {
      setDevice(data.energyMeterById as Partial<EnergyMeter>);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation[] = [
        {
          'Device Identification': {
            'Device Name': device.name ?? 'Unnamed Energy Meter',
            'Device ID': device.deviceId ?? 'Unidentified',
            'Device Type': device.typeDisplayName ?? 'Unknown',
            'Last Updated': moment(device.timestamp).fromNow(),
          },
          'Energy Metrics': {
            'Energy Consumed': formatDeviceMetric({
              value: device.energyConsumed?.value ?? undefined,
              units: device.energyConsumed?.units ?? undefined,
            }),
            'Power Demand': formatDeviceMetric({
              value: device.powerDemand?.value ?? undefined,
              units: device.powerDemand?.units ?? undefined,
            }),
          },
          'Power Quality': {
            Voltage: formatDeviceMetric({
              value: device.voltage?.value ?? undefined,
              units: device.voltage?.units ?? undefined,
            }),
            Current: formatDeviceMetric({
              value: device.current?.value ?? undefined,
              units: device.current?.units ?? undefined,
            }),
            'Power Factor': formatDeviceMetric({
              value: device.powerFactor?.value ?? undefined,
              units: device.powerFactor?.units ?? undefined,
            }),
            Frequency: formatDeviceMetric({
              value: device.frequency?.value ?? undefined,
              units: device.frequency?.units ?? undefined,
            }),
          },
          'System Status': {
            'Power State': device.powerState?.value ? 'On' : 'Off',
            'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
            'Is Configured': device.isConfigured ? 'Yes' : 'No',
            'Is Controlled': device.isControlled ? 'Yes' : 'No',
            'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
            'Output Enabled': device.outputEnabled?.value ? 'Yes' : 'No',
          },
          'System Integration': {
            Property: device.property?.title ?? 'Not assigned',
            Unit: device.unit?.name ?? 'Not assigned',
            Panel: device.panel?.displayName ?? 'Not assigned',
            Source: device.source?.name ?? 'Not assigned',
          },
        },
      ];
      setDeviceInfo(info);
    }
  }, [device]);

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return (
    <PanelFrame
      title="Energy Meter"
      iconType="mdi:meter-electric"
      device={device as Partial<Device>}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Energy Consumed
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.energyConsumed?.value ?? undefined,
                units: device.energyConsumed?.units ?? undefined,
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Power Demand
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.powerDemand?.value ?? undefined,
                units: device.powerDemand?.units ?? undefined,
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Output Status
            </Typography>
            <Typography variant="h6" align="center">
              {device.outputEnabled?.value ? 'Enabled' : 'Disabled'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PanelFrame>
  );
};
